import CheckAndReturn from './CheckAndReturn';
import { addCompanyLogoToPdf } from './pdfCompanyLogo';

const ROW_H = 8;
const FOOTER_BASE_RESERVE = 78;
const TABLE_HEADER_H = 8;
const PAGE_BOTTOM = 287;
const FIRST_PAGE_ITEMS_START_Y = 89;
const CONTINUATION_PAGE_ITEMS_START_Y = 23;
const ITEM_NAME_MAX_WIDTH = 50;
const ITEM_NAME_LINE_H = 3.8;

const DEFAULT_TERM_CONDITION = `Important : i) PO No. & PO date must be mentioned on all documents & correspondence, failing which the documents shall be void
ii) All material supplied against this PO shall be strictly as per specifications mentioned below or else shall be considered rejected
iii) Material Safety Data Sheet (MSDS), shelf life and deposition method to be provided as applicable`;

const ITEM_COLS = [
  { label: 'Sl.', xOffset: 0, w: 8, align: 'center' },
  { label: 'Item Code', xOffset: 8, w: 22, align: 'left' },
  { label: 'Item Name', xOffset: 30, w: 58, align: 'left' },
  { label: 'HSN/\nSAC', xOffset: 88, w: 22, align: 'center' },
  { label: 'Qty.', xOffset: 110, w: 15, align: 'center' },
  { label: 'Uom', xOffset: 125, w: 13, align: 'center' },
  { label: 'Basic Rate', xOffset: 138, w: 22, align: 'right' },
  { label: 'Dis%', xOffset: 160, w: 12, align: 'center' },
  { label: 'Amount', xOffset: 172, w: 18, align: 'right' },
];

const getPageMetrics = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginL = 10;
  const marginR = pageWidth - 10;
  return {
    pageWidth,
    marginL,
    marginR,
    contentWidth: marginR - marginL,
  };
};

const buildCols = (marginL) =>
  ITEM_COLS.map((col) => ({ ...col, x: marginL + col.xOffset }));

const getItemsList = (items, row) =>
  items && items.length > 0 ? items : row.item || [];

const resolveItemUom = (item) => {
  const isEmptyUnit = (value) =>
    value === undefined || value === null || value === '' || value === '0' || value === 0;

  if (!isEmptyUnit(item.uom)) return String(item.uom);
  if (!isEmptyUnit(item.packagingunit)) return String(item.packagingunit);

  const unitVal = item.unit ?? item.product?.unit;
  if (isEmptyUnit(unitVal)) return 'NOS';
  if (typeof unitVal === 'string') return unitVal.trim() || 'NOS';
  if (typeof unitVal === 'object') {
    return unitVal.units_name || unitVal.symbol || unitVal.name || 'NOS';
  }
  return 'NOS';
};

const computeTotals = (itemsToRender) => {
  let totalQty = 0;
  let totalAmt = 0;
  itemsToRender.forEach((item) => {
    const qty = parseFloat(item.qty || item.quantity || 0);
    const rate = parseFloat(item.rate || item.basic_rate || item.price || 0);
    const dis = parseFloat(item.discount || item.dis || 0);
    const amt = parseFloat(
      item.amount || item.total || qty * rate * (1 - dis / 100)
    );
    totalQty += qty;
    totalAmt += amt;
  });
  return { totalQty, totalAmt };
};

const getSupplierFields = (row) => ({
  supplierName: row.supplier
    ? row.supplier.fname ||
      row.supplier.contact_name ||
      row.supplier.supplier_name ||
      row.supplier.name ||
      ''
    : '',
  supplierCode: row.supplier
    ? row.supplier.sup_code ||
      row.supplier.supplier_code ||
      row.supplier.code ||
      row.supplier.vendor_code ||
      ''
    : '',
  supplierAddress: row.supplier
    ? row.supplier.address || row.supplier.supplier_address || ''
    : '',
  supplierGST: row.supplier
    ? row.supplier.gst_uin || row.supplier.gst_no || row.supplier.gstin || ''
    : '',
  poNo: row.purchase_order_no || '',
  poDate: CheckAndReturn.transformToDate2(row.created_at),
  effectFrom: CheckAndReturn.transformToDate2(row.created_at),
  validUpto: CheckAndReturn.transformToDate2(row.delivery_date),
  againstPO: row.against_po || '',
});

const drawPageNumber = (doc, pageNum, totalPages, marginR) => {
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.text(`Page ${pageNum}/${totalPages}`, marginR, 10, { align: 'right' });
};

const drawFullPageHeader = (doc, row, companyDetails, pageNum, totalPages) => {
  const { pageWidth, marginL, marginR, contentWidth } = getPageMetrics(doc);
  let y = 10;

  addCompanyLogoToPdf(doc, { x: marginL, y: 6 });

  const companyName =
    companyDetails?.CompanyName || 'NALANDA AUTOCOMP PRIVATE LIMITED';
  const companyAddressLines = companyDetails?.Address
    ? companyDetails.Address.split('\n')
    : [
        'Plot No.:243 Malur Industrial Area',
        'Name Of Premises/Building: KIADB Industrial Area, PHASE-III',
        'City/Town/Village: Malur Industrial Area, Kolar, 563130 (Karnataka)',
      ];

  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text(companyName, pageWidth / 2, y, { align: 'center' });
  y += 5;

  doc.setFontSize(7.5);
  doc.setFont(undefined, 'normal');
  companyAddressLines.forEach((line) => {
    if (line) {
      doc.text(line, pageWidth / 2, y, { align: 'center' });
      y += 4;
    }
  });

  drawPageNumber(doc, pageNum, totalPages, marginR);

  y += 3;
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('PURCHASE-ORDER', pageWidth / 2, y, { align: 'center' });
  y += 6;

  doc.setFillColor(220, 220, 220);
  doc.rect(marginL, y - 4, contentWidth, 6, 'F');
  doc.setFontSize(8.5);
  doc.setFont(undefined, 'bold');
  doc.text(
    'GST No.:29AAGCN9739C1Z3   CIN: U73100KA2020PTC134836',
    pageWidth / 2,
    y,
    { align: 'center' }
  );
  y += 6;

  const {
    supplierName,
    supplierCode,
    supplierAddress,
    supplierGST,
    poNo,
    poDate,
    effectFrom,
    validUpto,
    againstPO,
  } = getSupplierFields(row);

  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(marginL, y, contentWidth, 36);

  const labelX = marginL + 2;
  const valueX = marginL + 28;
  const rightLabelX = pageWidth / 2 + 2;
  const rightValueX = pageWidth / 2 + 30;

  doc.setFontSize(8);
  const infoRows = [
    ['Supplier Code', supplierCode, 'P.O. No.', poNo],
    ['Supplier Name', supplierName, 'P.O. Date', poDate],
    ['Supplier Address', supplierAddress, 'Effect From', effectFrom],
    ['', '', 'Valid Upto', validUpto],
    ['GST No.', supplierGST, 'Against PO', againstPO],
    ['Reference...', '', '', ''],
  ];

  let infoY = y + 5;
  infoRows.forEach(([lLabel, lValue, rLabel, rValue]) => {
    doc.setFont(undefined, 'bold');
    doc.text(lLabel, labelX, infoY);
    doc.setFont(undefined, 'normal');
    if (lValue) {
      doc.text(`: ${lValue}`, valueX, infoY, {
        maxWidth: pageWidth / 2 - valueX - 2,
      });
    }
    doc.setFont(undefined, 'bold');
    if (rLabel) doc.text(rLabel, rightLabelX, infoY);
    doc.setFont(undefined, 'normal');
    if (rValue) doc.text(`: ${rValue}`, rightValueX, infoY);
    infoY += 5;
  });

  y += 38;

  return y + 1;
};

const drawContinuationHeader = (doc, row, pageNum, totalPages) => {
  const { pageWidth, marginL, marginR } = getPageMetrics(doc);
  let y = 12;

  addCompanyLogoToPdf(doc, { x: marginL, y: 6, width: 24, height: 7 });
  drawPageNumber(doc, pageNum, totalPages, marginR);

  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('PURCHASE-ORDER (Continued)', pageWidth / 2, y, { align: 'center' });
  y += 5;

  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.text(`P.O. No.: ${row.purchase_order_no || ''}`, marginL, y);

  return y + 4;
};

const getTermsText = (companyDetails) => {
  const saved = companyDetails?.po_term_condition?.trim();
  return saved || DEFAULT_TERM_CONDITION;
};

const getTermsSectionHeight = (doc, companyDetails) => {
  const { contentWidth } = getPageMetrics(doc);
  doc.setFontSize(6.5);
  const lines = doc.splitTextToSize(getTermsText(companyDetails), contentWidth - 8);
  return 4 + 5 + 4 + lines.length * 3.5 + 4;
};

const getFooterReserve = (doc, companyDetails) =>
  FOOTER_BASE_RESERVE + getTermsSectionHeight(doc, companyDetails);

const getItemRowHeight = (doc, itemName) => {
  doc.setFontSize(8);
  const lines = doc.splitTextToSize(String(itemName || ''), ITEM_NAME_MAX_WIDTH);
  const textBlockH = lines.length * ITEM_NAME_LINE_H;
  return Math.max(ROW_H, 4 + textBlockH + 2);
};

const getItemRowHeights = (doc, items) =>
  items.map((item) => getItemRowHeight(doc, String(item.itemname || '')));

const fitItemsByHeight = (rowHeights, startIdx, maxHeight) => {
  let height = 0;
  let count = 0;

  for (let i = startIdx; i < rowHeights.length; i += 1) {
    const rowHeight = rowHeights[i];
    if (count > 0 && height + rowHeight > maxHeight) {
      break;
    }
    height += rowHeight;
    count += 1;
    if (height >= maxHeight) {
      break;
    }
  }

  return Math.max(count, rowHeights.length > startIdx ? 1 : 0);
};

const drawItemsTableHeader = (doc, y) => {
  const { marginL, marginR, contentWidth } = getPageMetrics(doc);
  const cols = buildCols(marginL);

  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(marginL, y, contentWidth, TABLE_HEADER_H);

  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  cols.forEach((col) => {
    const cx =
      col.align === 'center'
        ? col.x + col.w / 2
        : col.align === 'right'
          ? col.x + col.w - 1
          : col.x + 1;
    doc.text(col.label, cx, y + 4, {
      align: col.align === 'left' ? 'left' : col.align,
    });
    doc.line(col.x, y, col.x, y + TABLE_HEADER_H);
  });
  doc.line(marginR, y, marginR, y + TABLE_HEADER_H);
  doc.line(marginL, y + TABLE_HEADER_H, marginR, y + TABLE_HEADER_H);

  return y + TABLE_HEADER_H;
};

const drawItemRow = (doc, item, displayIndex, y) => {
  const { marginL, marginR } = getPageMetrics(doc);
  const cols = buildCols(marginL);

  const qty = parseFloat(item.qty || item.quantity || 0);
  const rate = parseFloat(item.rate || item.basic_rate || item.price || 0);
  const dis = parseFloat(item.discount || item.dis || 0);
  const amt = parseFloat(
    item.amount || item.total || qty * rate * (1 - dis / 100)
  );
  const hsn = String(item.hsn_code || 0);
  const uom = resolveItemUom(item);
  const itemCode = parseFloat(item.itemid) ? String(item.itemid) : '';
  const itemName = String(item.itemname || '');

  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);
  const nameLines = doc.splitTextToSize(itemName, ITEM_NAME_MAX_WIDTH);
  const rowH = Math.max(ROW_H, 4 + nameLines.length * ITEM_NAME_LINE_H + 2);

  doc.line(marginL, y, marginR, y);
  cols.forEach((col) => doc.line(col.x, y, col.x, y + rowH));
  doc.line(marginR, y, marginR, y + rowH);
  doc.line(marginL, y + rowH, marginR, y + rowH);

  const textY = y + 4.5;
  doc.text(String(displayIndex + 1), marginL + 4, textY, { align: 'center' });
  doc.text(itemCode, marginL + 9, textY, { maxWidth: 20 });
  nameLines.forEach((line, lineIdx) => {
    doc.text(line, marginL + 32, textY + lineIdx * ITEM_NAME_LINE_H);
  });
  doc.text(hsn, marginL + 99, textY, { align: 'center' });
  doc.text(qty.toFixed(2), marginL + 117, textY, { align: 'center' });
  doc.text(uom, marginL + 131, textY, { align: 'center' });
  doc.text(rate.toFixed(3), marginL + 159, textY, { align: 'right' });
  doc.text(dis.toFixed(1), marginL + 168, textY, { align: 'center' });
  doc.text(amt.toFixed(2), marginR - 1, textY, { align: 'right' });

  return y + rowH;
};

const drawFooterSection = (doc, row, y, totals, stageUser = {}, companyDetails = null) => {
  const { pageWidth, marginL, marginR, contentWidth } = getPageMetrics(doc);
  const { totalQty, totalAmt } = totals;

  const taxableValue = parseFloat(row.total_amount || totalAmt);
  const cgstRate = parseFloat(row.cgst_rate || 9);
  const sgstRate = parseFloat(row.sgst_rate || 9);
  const igstRate = parseFloat(row.igst_rate || 0);
  const cgstAmt = parseFloat(row.cgst_amount || (taxableValue * cgstRate) / 100);
  const sgstAmt = parseFloat(row.sgst_amount || (taxableValue * sgstRate) / 100);
  const igstAmt = parseFloat(row.igst_amount || 0);
  const totalValue = taxableValue + cgstAmt + sgstAmt + igstAmt;
  const freight = parseFloat(row.freight || 0);
  const loading = parseFloat(row.loading_unloading || 0);
  const packing = parseFloat(row.packing_forwarding || 0);

  doc.setFont(undefined, 'bold');
  doc.setFontSize(8);
  doc.line(marginL, y, marginR, y);
  doc.text('Remarks', marginL + 1, y + 4);
  doc.text(row.remarks || '', marginL + 22, y + 4, { maxWidth: contentWidth - 24 });
  doc.line(marginL, y + 6, marginR, y + 6);
  y += 10;

  doc.setFont(undefined, 'bold');
  doc.setFontSize(8.5);
  doc.line(marginL, y, marginR, y);
  doc.text('T O T A L --->>>', marginL + 2, y + 5);
  doc.text(totalQty.toFixed(2), marginL + 117, y + 5, { align: 'center' });
  doc.text(taxableValue.toFixed(2), marginR - 1, y + 5, { align: 'right' });
  doc.line(marginL, y + 7, marginR, y + 7);
  y += 10;

  const summaryLabelX = pageWidth / 2 + 10;
  const summaryValX = marginR - 1;

  const leftInfoItems = [
    ['Shipment by', row.ShipmentBy || row.shipment_by || ''],
    ['Payment Terms', row.pay_terms || row.payment_terms || ''],
    ['Delivery Date', CheckAndReturn.transformToDate2(row.delivery_date)],
    ['Insurance', row.insurance || ''],
    ['Others if (any)', row.others || ''],
  ];

  const rightSummaryItems = [
    ['Add Freight & Cartage', freight.toFixed(2), false],
    ['Loading & Unloading', loading.toFixed(2), false],
    ['Packing & Forwarding', packing.toFixed(2), false],
    ['Total Taxable Value', taxableValue.toFixed(2), true],
    [`IGST @ ${igstRate.toFixed(2)}%`, igstAmt.toFixed(2), false],
    [`CGST @ ${cgstRate.toFixed(2)}%`, cgstAmt.toFixed(2), false],
    [`SGST @ ${sgstRate.toFixed(2)}%`, sgstAmt.toFixed(2), false],
    ['Total Value', totalValue.toFixed(2), true],
  ];

  doc.setFontSize(7.5);
  let leftY = y;
  leftInfoItems.forEach(([label, value]) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, marginL + 2, leftY);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${value}`, marginL + 30, leftY);
    leftY += 5;
  });

  let rightY = y;
  rightSummaryItems.forEach(([label, value, bold]) => {
    doc.setFont(undefined, bold ? 'bold' : 'normal');
    doc.text(label, summaryLabelX, rightY);
    doc.text(':', summaryLabelX + 38, rightY);
    doc.text(value, summaryValX, rightY, { align: 'right' });
    rightY += 5;
  });

  doc.setFont(undefined, 'bold');
  doc.setFontSize(8);
  doc.text('For NALANDA AUTOCOMP PRIVATE LIMITED', summaryValX, rightY, {
    align: 'right',
  });

  y = Math.max(leftY, rightY) + 6;

  const sigLabels = ['Prepared BY', 'Checked By', 'Verified By', 'Approved By'];
  const sigWidth = contentWidth / 4;
  const sigBoxH = 12;
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);
  doc.rect(marginL, y, contentWidth, sigBoxH);

  const nameFields = [
    stageUser?.recheckBy?.name,
    stageUser?.recheckBy?.name || 'N/A',
    stageUser?.verifyBy?.name || 'N/A',
    stageUser?.approveBy?.name || 'N/A',
  ];

  sigLabels.forEach((lbl, i) => {
    const sx = marginL + i * sigWidth;
    if (i > 0) doc.line(sx, y, sx, y + sigBoxH);
    doc.text(lbl, sx + sigWidth / 2, y + 4, { align: 'center' });
    if (nameFields[i]) {
      doc.setFont(undefined, 'bold');
      doc.text(nameFields[i], sx + sigWidth / 2, y + 9, { align: 'center' });
      doc.setFont(undefined, 'normal');
    }
  });
  y += sigBoxH + 4;

  doc.setFontSize(6.5);
  const termLines = doc.splitTextToSize(getTermsText(companyDetails), contentWidth - 8);
  const termsBoxH = 5 + 4 + termLines.length * 3.5 + 4;
  doc.rect(marginL, y, contentWidth, termsBoxH);

  doc.setFontSize(7);
  doc.setFont(undefined, 'bold');
  doc.text('Terms & Conditions', marginL + 3, y + 4);

  doc.setFont(undefined, 'normal');
  let termY = y + 9;
  termLines.forEach((line) => {
    doc.text(line, marginL + 3, termY);
    termY += 3.5;
  });
  y += termsBoxH + 2;

  if (!row.latest_workflow_stage || row.latest_workflow_stage < 4) {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('UN-APPROVED P.O., REQUIRED SIGNATURE', marginL, y + 6);
    doc.setFontSize(8);
    doc.text('(Authorised Signatory)', marginR, y + 6, { align: 'right' });
  }
};

const getItemsStartY = (isFirstPageOfCopy) =>
  isFirstPageOfCopy ? FIRST_PAGE_ITEMS_START_Y : CONTINUATION_PAGE_ITEMS_START_Y;

const sumRowHeights = (rowHeights, startIdx, count) => {
  let total = 0;
  for (let i = startIdx; i < startIdx + count; i += 1) {
    total += rowHeights[i];
  }
  return total;
};

/** Plan how items + footer are split across pages for one copy. */
const planCopyPages = (doc, companyDetails, rowHeights) => {
  const itemCount = rowHeights.length;
  const footerReserve = getFooterReserve(doc, companyDetails);
  const pages = [];
  let idx = 0;

  while (idx < itemCount || pages.length === 0) {
    const isFirst = pages.length === 0;
    const itemsStartY = getItemsStartY(isFirst);
    const remaining = itemCount - idx;

    if (remaining === 0) {
      pages.push({
        startIdx: 0,
        count: 0,
        isFirstPageOfCopy: isFirst,
        footerOnly: true,
        drawFooter: true,
      });
      break;
    }

    const availableWithFooter =
      PAGE_BOTTOM - itemsStartY - TABLE_HEADER_H - footerReserve;
    const availableWithoutFooter =
      PAGE_BOTTOM - itemsStartY - TABLE_HEADER_H;
    const remainingHeight = sumRowHeights(rowHeights, idx, remaining);

    if (remainingHeight <= availableWithFooter) {
      pages.push({
        startIdx: idx,
        count: remaining,
        isFirstPageOfCopy: isFirst,
        footerOnly: false,
        drawFooter: true,
      });
      idx = itemCount;
    } else if (remainingHeight <= availableWithoutFooter) {
      pages.push({
        startIdx: idx,
        count: remaining,
        isFirstPageOfCopy: isFirst,
        footerOnly: false,
        drawFooter: false,
      });
      idx = itemCount;
      pages.push({
        startIdx: idx,
        count: 0,
        isFirstPageOfCopy: false,
        footerOnly: true,
        drawFooter: true,
      });
    } else {
      const count = fitItemsByHeight(rowHeights, idx, availableWithoutFooter);
      pages.push({
        startIdx: idx,
        count,
        isFirstPageOfCopy: isFirst,
        footerOnly: false,
        drawFooter: false,
      });
      idx += count;
    }
  }

  return pages;
};

const drawSingleCopy = (
  doc,
  row,
  itemsToRender,
  totals,
  stageUser,
  companyDetails,
  pageNumOffset,
  totalPages,
  hasDrawnContent
) => {
  const rowHeights = getItemRowHeights(doc, itemsToRender);
  const pages = planCopyPages(doc, companyDetails, rowHeights);
  let pageNum = pageNumOffset;
  let contentStarted = hasDrawnContent;

  pages.forEach((pagePlan) => {
    if (contentStarted) {
      doc.addPage();
    }
    contentStarted = true;
    pageNum += 1;

    let y;
    if (pagePlan.isFirstPageOfCopy) {
      y = drawFullPageHeader(doc, row, companyDetails, pageNum, totalPages);
    } else {
      y = drawContinuationHeader(doc, row, pageNum, totalPages);
    }

    if (!pagePlan.footerOnly) {
      y = drawItemsTableHeader(doc, y);
    }

    for (let i = 0; i < pagePlan.count; i += 1) {
      y = drawItemRow(
        doc,
        itemsToRender[pagePlan.startIdx + i],
        pagePlan.startIdx + i,
        y
      );
    }

    if (pagePlan.drawFooter) {
      drawFooterSection(doc, row, y, totals, stageUser, companyDetails);
    }
  });

  return pages.length;
};

/**
 * Draw purchase order PDF with automatic page breaks for long item lists.
 * copyCount = number of duplicate copies (1–4 from print dialog).
 */
export const drawPurchaseOrderToPdf = (
  doc,
  { row, items, copyCount = 1, stageUser = {}, companyDetails = null }
) => {
  const itemsToRender = getItemsList(items, row);
  const totals = computeTotals(itemsToRender);
  const rowHeights = getItemRowHeights(doc, itemsToRender);
  const pagesPerCopy = planCopyPages(doc, companyDetails, rowHeights).length;
  const totalPages = pagesPerCopy * copyCount;

  let pageNumOffset = 0;
  let hasDrawnContent = false;

  for (let copy = 0; copy < copyCount; copy += 1) {
    const pagesDrawn = drawSingleCopy(
      doc,
      row,
      itemsToRender,
      totals,
      stageUser,
      companyDetails,
      pageNumOffset,
      totalPages,
      hasDrawnContent
    );
    pageNumOffset += pagesDrawn;
    hasDrawnContent = true;
  }
};

export default drawPurchaseOrderToPdf;
